// English translations (default)
const en = {
  // Common
  app: {
    title: "Class Manager",
    loading: "Loading...",
  },
  common: {
    cancel: "Cancel",
    confirm: "Confirm",
    save: "Save",
    delete: "Delete",
    close: "Close",
    rowsPerPage: "Rows per page:",
    create: "Create",
    update: "Update",
    goBack: "Go Back",
    edit: "Edit",
    link: "Link",
  },
  // Navigation
  nav: {
    dashboard: "Dashboard",
    calendar: "Calendar",
    addClass: "New Class",
    reports: "Reports",
    settings: "Settings",
    manageUsers: "Manage Users",
    staffRoles: "Staff Roles",
  },
  // Dashboard
  dashboard: {
    quickActions: "Quick Actions",
    viewSchedule: "View Schedule",
    manageSchedule: "Manage your teaching timetable",
    addClass: "Add Class",
    newSession: "Record a new teaching session",
    generateReport: "Generate Report",
    createReports: "Create monthly salary reports",
    monthSummary: "This Month's Summary",
    totalClasses: "Total Classes",
    totalHours: "Total Hours",
    totalSalary: "Total Salary",
    viewFullReport: "View Full Report",
    todayClasses: "Today's Classes",
    noClasses: "No classes scheduled for today",
    position: "Position",
    edit: "Edit",
    viewFullSchedule: "View Full Schedule",
    quickPromote: "Quick User Promotion",
    userIdToPromote: "User ID to Promote",
    enterUserId: "Enter user ID to promote to manager",
    promote: "Promote",
    promoteHelp:
      "This will immediately grant manager privileges to the specified user. The user ID can be found in the Manage Users section.",
    promoteSuccess:
      "User promoted to manager successfully. They will need to sign out and sign back in to access manager features.",
    promoteError: "Failed to promote user. Please check the user ID and try again.",
  },
  // Authentication
  auth: {
    forgotPassword: "Forgot Password",
    resetPassword: "Reset Password",
    resetInstructions:
      "Enter your email address, and we'll send you a link to reset your password.",
    resetSuccess: "Password reset email sent. Please check your inbox.",
    resetError:
      "Failed to send password reset email. Please check your email address and try again.",
    backToLogin: "Back to Login",
    sendResetLink: "Send Reset Link",
    passwordRequirements: "Password must be at least 6 characters long",
  },
  // Settings
  settings: {
    accountSettings: "Account Settings",
    profileInfo: "Profile Information",
    displayName: "Display Name",
    email: "Email",
    accountCreated: "Account Created",
    notSet: "Not set",
    appPreferences: "Application Preferences",
    language: "Language",
    english: "English",
    vietnamese: "Tiếng Việt",
    backToDashboard: "Back to Dashboard",
    signOut: "Sign Out",
    role: "User Role",
    roleTeacher: "Teacher",
    roleManager: "Manager",
    managerInfo: "As a manager, you can access additional management features.",
    passwordSettings: "Password Settings",
    currentPassword: "Current Password",
    newPassword: "New Password",
    confirmPassword: "Confirm New Password",
    changePassword: "Change Password",
    passwordChanged: "Password changed successfully",
    passwordError: "Failed to change password. Please check your current password.",
    passwordMismatch: "New passwords do not match",
    passwordTooShort: "Password must be at least 6 characters long",
    passwordCurrent: "Current password is required",
    updatePassword: "Update Password",
    resetPassword: "Reset Password",
    processingRequest: "Processing...",
  },
  // Reports
  reports: {
    salaryReports: "Salary Reports",
    generateNew: "Generate New Report",
    month: "Month",
    year: "Year",
    generating: "Generating...",
    generate: "Generate Report",
    previousReports: "Previous Reports",
    noPrevious: "No previous reports found",
    unable: "Unable to load previous reports",
    createIndex: "Create Required Index",
    report: "Report",
    exportPdf: "Export PDF",
    week: "Week",
    class: "Class",
    day: "Day",
    time: "Time",
    date: "Date",
    hours: "Hours",
    baseRate: "Base Rate",
    salary: "Salary",
    selectUser: "Select User for Report Generation",
    viewingReportsFor: "Viewing reports for",
    selectUserPlaceholder: "Select a user to view their reports",
    managerFeature: "Manager Feature",
    generatedAt: "Generated at",
    generatedBy: "Generated by",
    yourReport: "Your Report",
    you: "You",
    anotherUser: "Another User",
    exportGSheets: "Export to Google Sheets",
    exporting: "Exporting...",
    openSheet: "Open Google Sheet",
    exportSuccess: "Export successful",
    exportError: "Export failed",
  },
  // Class Form
  classForm: {
    addClass: "Add New Class",
    editClass: "Edit Class",
    className: "Class Name",
    date: "Date",
    startTime: "Start Time",
    endTime: "End Time",
    day: "Day",
    baseRate: "Base Rate (VND)",
    weekOfMonth: "Week of Month",
    notes: "Notes",
    delete: "Delete",
    cancel: "Cancel",
    saving: "Saving...",
    save: "Save Changes",
    add: "Add Class",
    confirmDelete: "Are you sure you want to delete this class?",
    repeatClass: "Make this a repeating class",
    repeatType: "Repeat Frequency",
    weekly: "Weekly",
    biweekly: "Bi-weekly",
    monthly: "Monthly",
    repeatUntil: "Repeat Until",
    repeatFrom: "Repeat From",
    repeatTo: "Repeat To",
    repeatRange: "Repeat Range",
    repeatInfo: "This will create multiple class instances based on the repeat settings.",
    dateRangeInfo: "Classes will repeat between the selected dates",
    deleteDescription:
      'Are you sure you want to delete the class "{className}"? This action cannot be undone.',
    classType: "Class Type",
    regularClass: "Regular Class",
    makeupClass: "Makeup Class",
    regularClassInfo:
      "A regular class repeats weekly at the same time. It will appear in the calendar on each occurrence.",
    makeupClassInfo: "A makeup class is a one-time class that doesn't repeat.",
    makeup: "Makeup",
    repeatEach: "Repeats each",
    at: "at",
    assignedTo: "Assigned to",
    confirmGeneric: "Are you sure you want to proceed with this action?",
    confirmSaveTitle: "Save Changes?",
    confirmSaveDescription: "How would you like to save these changes?",
    confirmDeleteTitle: "Delete Class?",
    thisInstanceOnly: "Apply to this instance only",
    thisAndFutureInstances: "Apply to this and all future instances",
    changeScope: "Apply changes to",
    currentOnly: "This instance only",
    currentAndFuture: "This and all future instances",
  },
  // Days
  days: {
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    friday: "Friday",
    saturday: "Saturday",
    sunday: "Sunday",
    short: {
      monday: "Mon",
      tuesday: "Tue",
      wednesday: "Wed",
      thursday: "Thur",
      friday: "Fri",
      saturday: "Sat",
      sunday: "Sun",
    },
    ultra: {
      monday: "M",
      tuesday: "T",
      wednesday: "W",
      thursday: "T",
      friday: "F",
      saturday: "Sa",
      sunday: "S",
    },
  },
  // Calendar
  calendar: {
    confirmMove: "Do you want to reschedule this class?",
    moveSuccess: "Class rescheduled successfully",
    moveError: "Failed to reschedule class",
    shortDateFormat: "D/M",
    shortTimeFormat: "h:mm",
    filter: "Filter",
    myClasses: "My Classes",
    assignedClasses: "Classes I'm Assigned To",
    globalClasses: "Global Classes",
    listView: "List View",
    calendarView: "Calendar View",
    search: "Search",
    searchPlaceholder: "Search by name, position...",
    noClassesFound: "No classes found",
    editClass: "Edit",
    assignUsers: "Assign Users",
    dateTime: "Date & Time",
    creator: "Creator",
    assignments: "Assignments",
    actions: "Actions",
    sortBy: "Sort By",
    ascending: "Ascending",
    descending: "Descending",
    assignStaff: "Assign Staff",
    schedule: "Schedule",
    person: "person",
    people: "people",
    confirmRescheduleTitle: "Reschedule Class",
    confirmRescheduleDescription: "How would you like to apply this schedule change?",
    managerOnlyAdd: "Only managers can create new classes",
    managerOnlyEdit: "Only managers or the class creator can edit classes",
  },
  // User Management
  manageUsers: {
    title: "Manage Users",
    description: "View and manage user accounts and permissions",
    search: "Search by name or email",
    name: "Name",
    email: "Email",
    role: "Role",
    actions: "Actions",
    noUsers: "No users found",
    unnamed: "Unnamed User",
    roleManager: "Manager",
    roleTeacher: "Teacher",
    roleUser: "User",
    promoteTooltip: "Promote to Manager",
    demoteTooltip: "Demote to Teacher",
    promoteTitle: "Promote User to Manager",
    promoteDescription:
      "Are you sure you want to promote {email} to a Manager role? They will have access to user management features.",
    demoteTitle: "Demote User to Teacher",
    demoteDescription:
      "Are you sure you want to demote {email} to a Teacher role? They will lose access to management features.",
    promote: "Promote to Manager",
    demote: "Demote to Teacher",
    promoteSuccess: "User successfully promoted to Manager",
    demoteSuccess: "User successfully demoted to Teacher",
    roleChangeError: "Failed to change user role: {error}",
    // New entries for combined interface
    combined: {
      title: "User & Staff Management",
      description: "Manage user accounts, staff profiles, and role assignments in one place",
      userTab: "User Accounts",
      staffTab: "Staff Profiles",
      linkedAccounts: "Linked Accounts",
      userCount: "{count} users",
      staffCount: "{count} staff members",
      linkStatus: "Link Status",
      linked: "Linked",
      notLinked: "Not Linked",
      linkedTo: "Linked to: {name}",
      filterUsers: "Filter users",
      filterStaff: "Filter staff",
      staffMembers: "Staff Members",
      userAccounts: "User Accounts",
      assignStaff: "Assign Staff Profile",
      createStaffProfile: "Create Staff Profile",
      selectStaffToLink: "Select Staff to Link",
      linkToNewStaff: "Link to New Staff Profile",
      unassignedUsers: "Unassigned Users",
      unlinkedStaff: "Unlinked Staff Profiles",
      linkedUsers: "Linked Users",
      userDetails: "User Details",
      staffDetails: "Staff Details",
      linkActionSuccess: "Successfully linked user to staff profile",
      unlinkActionSuccess: "Successfully unlinked user from staff profile",
      userIdCopied: "User ID copied to clipboard",
      staffIdCopied: "Staff ID copied to clipboard",
    },
  },
  // Class Assignment
  classAssignment: {
    title: "Class Assignment",
    teachers: "Teachers",
    teachingAssistants: "Teaching Assistants",
    noTeachers: "No teachers assigned yet",
    noTAs: "No teaching assistants assigned yet",
    addUser: "Add User",
    selectUser: "Select a user",
    selectStaff: "Select Staff",
    role: "Role",
    teacher: "Teacher",
    ta: "Teaching Assistant",
    add: "Add",
    alreadyAssigned: "This user is already assigned to this class",
    userAdded: "User assigned successfully",
    userRemoved: "User removed successfully",
    addError: "Failed to add user",
    removeError: "Failed to remove user",
    globalClass: "Global Class (visible to all managers)",
    privateClass: "Private Class",
    makeGlobal: "Make global for all managers",
    globalSuccess: "Class is now visible to all managers",
    privateSuccess: "Class is now private",
    globalError: "Failed to change class visibility",
    managerView: "Manager View",
  },
  // Staff Roles Management
  staffRoles: {
    title: "Staff Roles Management",
    description: "Create and manage staff accounts, roles, and permissions",
    createStaff: "Create New Staff",
    createStaffAccount: "Create Staff Account",
    staffList: "Staff List",
    search: "Search by name or email",
    name: "Name",
    fullName: "Full Name",
    email: "Email",
    role: "Role",
    baseRate: "Base Rate",
    contactInfo: "Contact Info",
    phoneNumber: "Phone Number",
    zalo: "Zalo",
    facebook: "Facebook",
    position: "Position",
    positionTeacher: "Teacher",
    positionTA: "Teaching Assistant",
    positionAdmin: "Administrative Staff",
    baseRateHelp: "Base hourly rate in VND",
    actions: "Actions",
    noStaff: "No staff found",
    unnamed: "Unnamed Staff",
    roleManager: "Manager",
    roleTeacher: "Teacher",
    edit: "Edit",
    delete: "Delete",
    addStaff: "Add New Staff",
    editStaff: "Edit Staff Profile",
    staffDetails: "Staff Details",
    displayName: "Display Name",
    defaultRate: "Default Base Rate (VND)",
    assignUserAccount: "Assign User Account",
    createUserAccount: "Create User Account",
    userAccountHelp:
      "This will create a new user account that will be linked to this staff profile",
    selectUser: "Select User Account",
    noUserAccount: "No User Account",
    createSuccess: "Staff profile created successfully",
    updateSuccess: "Staff profile updated successfully",
    deleteSuccess: "Staff profile deleted successfully",
    promotedSuccess: "{name} has been promoted to Manager",
    demotedSuccess: "{name} has been demoted to Teacher",
    roleChangeError: "Failed to change user role: {error}",
    refreshError: "Failed to refresh data",
    error: "An error occurred. Please try again.",
    confirmDelete: "Delete Staff Profile",
    confirmDeleteDescription:
      "Are you sure you want to delete this staff profile? This action cannot be undone.",
    staffTab: "Staff",
    rolesTab: "User Roles",
    staffManagement: "Staff Management",
    accessDenied: "Access Denied",
    managerRoleRequired: "You must be a manager to access this section.",
    promoteUser: "Promote User",
    demoteUser: "Demote User",
    roleChangeWarning: "This will change the user's permissions and access level.",
    confirmPromotion: "Are you sure you want to promote {name} to Manager role?",
    confirmDemotion: "Are you sure you want to demote {name} to Teacher role?",
    promote: "Promote",
    demote: "Demote",
    unlinkSuccess: "User unlinked successfully",
    unlinkError: "Failed to unlink user: {error}",
    active: "Active",
    inactive: "Inactive",
    notProvided: "Not provided",
    accountStatus: "Account Status",
    linked: "Linked to user account",
    noAccount: "No user account",
    unlink: "Unlink account",
    link: "Link to account",
    createFirstStaff: "Create First Staff Member",
    noStaffDescription: "Create your first staff member to get started.",
    roles: "User Roles",
    roleUser: "Regular User",
    currentRole: "Current Role",
    selectUserHelp: "Select a user account to link to this staff member",
  },
  // Staff Management
  staff: {
    title: "Staff Management",
    description: "Create and manage staff members for class assignments",
    addStaff: "Add Staff",
    editStaff: "Edit Staff",
    staffDetails: "Staff Details",
    name: "Name",
    fullName: "Full Name",
    email: "Email",
    phone: "Phone Number",
    position: "Position",
    baseRate: "Base Rate",
    status: "Status",
    actions: "Actions",
    active: "Active",
    inactive: "Inactive",
    noStaffFound: "No staff records found",
    positions: {
      teacher: "Teacher",
      teachingAssistant: "Teaching Assistant",
      adminStaff: "Administrative Staff",
    },
    contactInfo: "Contact Information",
    address: "Address",
    emergencyContact: "Emergency Contact",
    socialMedia: "Social Media",
    facebook: "Facebook",
    zalo: "Zalo",
    notes: "Notes",
    saveSuccess: "Staff record saved successfully",
    deleteSuccess: "Staff record deleted successfully",
    deleteConfirm: "Are you sure you want to delete this staff record?",
    statusToggle: "Toggle status",
    setActive: "Set as active",
    setInactive: "Set as inactive",
    userAccount: "User Account",
    linkedAccount: "Linked Account",
    noAccount: "No Account",
    link: "Link",
    unlink: "Unlink",
    createAccount: "Create Account",
    linkToAccount: "Link to User Account",
    selectUser: "Select User",
    noAvailableUsers: "No available users to link",
    availableUsers: "Available Users",
    userAlreadyLinked: "This user is already linked to another staff profile",
    linkSuccess: "User successfully linked to staff profile",
    linkError: "Failed to link user to staff profile",
    unlinkSuccess: "User successfully unlinked from staff profile",
    unlinkConfirm: "Unlink User Account",
    unlinkConfirmMessage:
      "Are you sure you want to unlink this user account from the staff profile? The user will lose staff permissions.",
    assignments: "Class Assignments",
    noAssignments: "No classes assigned to this staff member",
    asTeacher: "As Teacher",
    asTA: "As Teaching Assistant",
    customRates: "Custom Rates",
    baseRateHelp: "Base hourly rate in your currency",
    customRateHelp: "Custom rate for this specific class",
    tabs: {
      all: "All Staff",
      active: "Active",
      inactive: "Inactive",
    },
    search: "Search by name, phone, or email",
    selectUserHelp: "Select a user account to link to this staff member",
    unlinkConfirm: "Are you sure you want to unlink this staff from their user account?",
    unlinkSuccess: "User unlinked successfully",
    linkSuccess: "User linked successfully",
    errors: {
      nameRequired: "Staff name is required",
      phoneFormat: "Invalid phone number format",
      emailFormat: "Invalid email format",
      rateNotNegative: "Rate cannot be negative",
      saveFailed: "Failed to save staff record",
      deleteFailed: "Failed to delete staff record",
      linkFailed: "Failed to link staff to user account",
      unlinkFailed: "Failed to unlink staff from user account",
      notFound: "Staff record not found",
    },
  },
  validation: {
    required: "This field is required",
    invalidEmail: "Please enter a valid email address",
    invalidRate: "Please enter a valid rate",
    invalidPhone: "Please enter a valid phone number",
    passwordMismatch: "Passwords do not match",
    passwordTooShort: "Password must be at least 6 characters",
  },
  roles: {
    manager: "Manager",
    teacher: "Teacher",
    teachingAssistant: "Teaching Assistant",
    staff: "Staff",
    user: "User",
  },
  errors: {
    staffNotFound: "Staff not found",
    assignmentFailed: "Failed to assign staff to class",
  },
  staffAssignment: {
    classAssignments: "Class Assignments",
    baseRate: "Base Rate",
    customRate: "Custom Rate",
    useCustomRate: "Use Custom Rate",
    noClassesAssigned: "No classes assigned",
    confirmRemoval: "Remove from Class",
    confirmRemovalContent: "Are you sure you want to remove {staff} from {className}?",
    assignStaffToClass: "Assign Staff to {className}",
    linkStaffToClass: "Link Staff to Class",
    selectStaff: "Select Staff",
    selectClass: "Select Class",
    selectRole: "Select Role",
    staffAdded: "Staff successfully assigned to class",
    staffRemoved: "Staff removed from class",
    hourlyRate: "Hourly Rate",
    rateInfo: "The base rate is used unless a custom rate is specified",
    overrideRate: "Override base rate",
    assignmentDate: "Assignment Date",
    removing: "Removing staff assignment...",
    adding: "Adding staff assignment...",
  },
};

// Vietnamese translations
const vi = {
  // Common
  app: {
    title: "Quản Lý Lớp Học",
    loading: "Đang tải...",
  },
  common: {
    cancel: "Hủy",
    confirm: "Xác nhận",
    save: "Lưu",
    delete: "Xóa",
    close: "Đóng",
    rowsPerPage: "Dòng mỗi trang:",
    create: "Tạo",
    update: "Cập Nhật",
    goBack: "Quay Lại",
    edit: "Sửa",
    link: "Liên Kết",
  },
  // Navigation
  nav: {
    dashboard: "Trang chủ",
    calendar: "Lịch học",
    addClass: "Lớp Mới",
    reports: "Báo Cáo",
    settings: "Cài Đặt",
    manageUsers: "Quản Lý Người Dùng",
    staffRoles: "Vai Trò Nhân Viên",
  },
  // Dashboard
  dashboard: {
    quickActions: "Thao Tác Nhanh",
    viewSchedule: "Xem Lịch Dạy",
    manageSchedule: "Quản lý lịch dạy của bạn",
    addClass: "Thêm Lớp",
    newSession: "Ghi lại một buổi học mới",
    generateReport: "Tạo Báo Cáo",
    createReports: "Tạo báo cáo lương hàng tháng",
    monthSummary: "Tổng Kết Tháng Này",
    totalClasses: "Tổng Số Lớp",
    totalHours: "Tổng Số Giờ",
    totalSalary: "Tổng Lương",
    viewFullReport: "Xem Báo Cáo Đầy Đủ",
    todayClasses: "Lớp Học Hôm Nay",
    noClasses: "Không có lớp nào được lên lịch hôm nay",
    position: "Vị trí",
    edit: "Sửa",
    viewFullSchedule: "Xem Lịch Đầy Đủ",
    quickPromote: "Thăng Cấp Người Dùng Nhanh",
    userIdToPromote: "ID Người Dùng Cần Thăng Cấp",
    enterUserId: "Nhập ID người dùng để thăng cấp lên quản lý",
    promote: "Thăng Cấp",
    promoteHelp:
      "Điều này sẽ ngay lập tức cấp đặc quyền quản lý cho người dùng được chỉ định. ID người dùng có thể được tìm thấy trong phần Quản Lý Người Dùng.",
    promoteSuccess:
      "Người dùng đã được thăng cấp lên quản lý thành công. Họ sẽ cần đăng xuất và đăng nhập lại để truy cập các tính năng quản lý.",
    promoteError: "Không thể thăng cấp người dùng. Vui lòng kiểm tra ID người dùng và thử lại.",
  },
  // Authentication
  auth: {
    forgotPassword: "Quên Mật Khẩu",
    resetPassword: "Đặt Lại Mật Khẩu",
    resetInstructions:
      "Nhập địa chỉ email của bạn, và chúng tôi sẽ gửi cho bạn một liên kết để đặt lại mật khẩu.",
    resetSuccess: "Email đặt lại mật khẩu đã được gửi. Vui lòng kiểm tra hộp thư đến của bạn.",
    resetError:
      "Không thể gửi email đặt lại mật khẩu. Vui lòng kiểm tra địa chỉ email của bạn và thử lại.",
    backToLogin: "Quay Lại Đăng Nhập",
    sendResetLink: "Gửi Liên Kết Đặt Lại",
    passwordRequirements: "Mật khẩu phải có ít nhất 6 ký tự",
  },
  // Settings
  settings: {
    accountSettings: "Cài Đặt Tài Khoản",
    profileInfo: "Thông Tin Hồ Sơ",
    displayName: "Tên Hiển Thị",
    email: "Email",
    accountCreated: "Ngày Tạo Tài Khoản",
    notSet: "Chưa đặt",
    appPreferences: "Tùy Chọn Ứng Dụng",
    language: "Ngôn Ngữ",
    english: "English",
    vietnamese: "Tiếng Việt",
    backToDashboard: "Quay Lại Trang chủ",
    signOut: "Đăng Xuất",
    role: "Vai Trò",
    roleTeacher: "Giáo Viên",
    roleManager: "Quản Lý",
    managerInfo: "Với vai trò quản lý, bạn có thể truy cập các tính năng quản lý bổ sung.",
    passwordSettings: "Cài Đặt Mật Khẩu",
    currentPassword: "Mật Khẩu Hiện Tại",
    newPassword: "Mật Khẩu Mới",
    confirmPassword: "Xác Nhận Mật Khẩu Mới",
    changePassword: "Đổi Mật Khẩu",
    passwordChanged: "Đổi mật khẩu thành công",
    passwordError: "Không thể đổi mật khẩu. Vui lòng kiểm tra mật khẩu hiện tại của bạn.",
    passwordMismatch: "Mật khẩu mới không khớp",
    passwordTooShort: "Mật khẩu phải có ít nhất 6 ký tự",
    passwordCurrent: "Mật khẩu hiện tại là bắt buộc",
    updatePassword: "Cập Nhật Mật Khẩu",
    resetPassword: "Đặt Lại Mật Khẩu",
    processingRequest: "Đang xử lý...",
  },
  // Reports
  reports: {
    salaryReports: "Báo Cáo Lương",
    generateNew: "Tạo Báo Cáo Mới",
    month: "Tháng",
    year: "Năm",
    generating: "Đang tạo...",
    generate: "Tạo Báo Cáo",
    previousReports: "Báo Cáo Trước",
    noPrevious: "Không tìm thấy báo cáo trước",
    unable: "Không thể tải báo cáo trước",
    createIndex: "Tạo Chỉ Mục Cần Thiết",
    report: "Báo Cáo",
    exportPdf: "Xuất PDF",
    week: "Tuần",
    class: "Lớp",
    day: "Ngày",
    time: "Thời Gian",
    date: "Ngày",
    hours: "Giờ",
    baseRate: "Lương Cơ Bản",
    salary: "Lương",
    selectUser: "Chọn Người Dùng để Tạo Báo Cáo",
    viewingReportsFor: "Đang xem báo cáo cho",
    selectUserPlaceholder: "Chọn người dùng để xem báo cáo của họ",
    managerFeature: "Tính Năng Quản Lý",
    generatedAt: "Được tạo lúc",
    generatedBy: "Được tạo bởi",
    yourReport: "Báo Cáo Của Bạn",
    you: "Bạn",
    anotherUser: "Người Dùng Khác",
    exportGSheets: "Xuất sang Google Sheets",
    exporting: "Đang xuất...",
    openSheet: "Mở Google Sheet",
    exportSuccess: "Xuất thành công",
    exportError: "Xuất thất bại",
  },
  // Class Form
  classForm: {
    addClass: "Thêm Lớp Mới",
    editClass: "Chỉnh Sửa Lớp",
    className: "Tên Lớp",
    date: "Ngày",
    startTime: "Giờ Bắt Đầu",
    endTime: "Giờ Kết Thúc",
    day: "Thứ",
    baseRate: "Lương Cơ Bản (VND)",
    weekOfMonth: "Tuần Trong Tháng",
    notes: "Ghi Chú",
    delete: "Xóa",
    cancel: "Hủy",
    saving: "Đang lưu...",
    save: "Lưu Thay Đổi",
    add: "Thêm Lớp",
    confirmDelete: "Bạn có chắc chắn muốn xóa lớp học này không?",
    repeatClass: "Làm cho lớp này lặp lại",
    repeatType: "Tần suất lặp lại",
    weekly: "Hàng tuần",
    biweekly: "Hai tuần một lần",
    monthly: "Hàng tháng",
    repeatUntil: "Lặp lại đến",
    repeatFrom: "Lặp lại từ",
    repeatTo: "Lặp lại đến",
    repeatRange: "Khoảng thời gian lặp lại",
    repeatInfo: "Điều này sẽ tạo nhiều lớp học dựa trên cài đặt lặp lại.",
    dateRangeInfo: "Các lớp học sẽ lặp lại trong khoảng thời gian đã chọn",
    deleteDescription:
      'Bạn có chắc chắn muốn xóa lớp "{className}"? Hành động này không thể hoàn tác.',
    classType: "Loại Lớp",
    regularClass: "Lớp Thường Xuyên",
    makeupClass: "Lớp Bù",
    regularClassInfo:
      "Lớp thường xuyên lặp lại hàng tuần vào cùng một thời điểm. Nó sẽ xuất hiện trong lịch mỗi lần lặp lại.",
    makeupClassInfo: "Lớp bù là lớp học một lần không lặp lại.",
    makeup: "Bù",
    repeatEach: "Lặp lại mỗi",
    at: "lúc",
    assignedTo: "Đã phân công",
    confirmGeneric: "Bạn có chắc chắn muốn tiếp tục với hành động này?",
    confirmSaveTitle: "Lưu Thay Đổi?",
    confirmSaveDescription: "Bạn muốn lưu các thay đổi này như thế nào?",
    confirmDeleteTitle: "Xóa Lớp Học?",
    thisInstanceOnly: "Chỉ áp dụng cho buổi học này",
    thisAndFutureInstances: "Áp dụng cho buổi học này và tất cả buổi học trong tương lai",
    changeScope: "Áp dụng thay đổi cho",
    currentOnly: "Chỉ buổi học này",
    currentAndFuture: "Buổi học này và tất cả buổi học trong tương lai",
  },
  // Days
  days: {
    monday: "Thứ 2",
    tuesday: "Thứ 3",
    wednesday: "Thứ 4",
    thursday: "Thứ 5",
    friday: "Thứ 6",
    saturday: "Thứ 7",
    sunday: "Chủ Nhật",
    short: {
      monday: "T2",
      tuesday: "T3",
      wednesday: "T4",
      thursday: "T5",
      friday: "T6",
      saturday: "T7",
      sunday: "CN",
    },
    ultra: {
      monday: "T2",
      tuesday: "T3",
      wednesday: "T4",
      thursday: "T5",
      friday: "T6",
      saturday: "T7",
      sunday: "CN",
    },
  },
  // Calendar
  calendar: {
    confirmMove: "Bạn có muốn lên lịch lại lớp học này không?",
    moveSuccess: "Đã lên lịch lại lớp học thành công",
    moveError: "Không thể lên lịch lại lớp học",
    shortDateFormat: "D/M",
    shortTimeFormat: "h:mm",
    filter: "Bộ Lọc",
    myClasses: "Lớp Học Của Tôi",
    assignedClasses: "Lớp Học Được Phân Công Cho Tôi",
    globalClasses: "Lớp Học Toàn Cục",
    listView: "Xem Danh Sách",
    calendarView: "Xem Lịch",
    search: "Tìm Kiếm",
    searchPlaceholder: "Tìm theo tên, vị trí...",
    noClassesFound: "Không tìm thấy lớp học nào",
    editClass: "Chỉnh Sửa",
    assignUsers: "Phân Công",
    dateTime: "Ngày & Giờ",
    creator: "Người Tạo",
    assignments: "Người Được Phân Công",
    actions: "Thao Tác",
    sortBy: "Sắp Xếp Theo",
    ascending: "Tăng Dần",
    descending: "Giảm Dần",
    assignStaff: "Phân Công Nhân Viên",
    schedule: "Lịch Trình",
    person: "người",
    people: "người",
    confirmRescheduleTitle: "Lên Lịch Lại Lớp Học",
    confirmRescheduleDescription: "Bạn muốn áp dụng thay đổi lịch này như thế nào?",
    managerOnlyAdd: "Chỉ quản lý mới có thể tạo lớp học mới",
    managerOnlyEdit: "Chỉ quản lý hoặc người tạo lớp mới có thể chỉnh sửa lớp học",
  },
  // User Management
  manageUsers: {
    title: "Quản Lý Người Dùng",
    description: "Xem và quản lý tài khoản và quyền của người dùng",
    search: "Tìm kiếm theo tên hoặc email",
    name: "Tên",
    email: "Email",
    role: "Vai Trò",
    actions: "Hành Động",
    noUsers: "Không tìm thấy người dùng",
    unnamed: "Người Dùng Chưa Đặt Tên",
    roleManager: "Quản Lý",
    roleTeacher: "Giáo Viên",
    promoteTooltip: "Thăng Cấp lên Quản Lý",
    demoteTooltip: "Giáng Cấp xuống Giáo Viên",
    promoteTitle: "Thăng Cấp Người Dùng lên Quản Lý",
    promoteDescription:
      "Bạn có chắc chắn muốn thăng cấp {email} lên vai trò Quản Lý? Họ sẽ có quyền truy cập vào các tính năng quản lý người dùng.",
    demoteTitle: "Giáng Cấp Người Dùng xuống Giáo Viên",
    demoteDescription:
      "Bạn có chắc chắn muốn giáng cấp {email} xuống vai trò Giáo Viên? Họ sẽ mất quyền truy cập vào các tính năng quản lý.",
    promote: "Thăng Cấp lên Quản Lý",
    demote: "Giáng Cấp xuống Giáo Viên",
    promoteSuccess: "Người dùng đã được thăng cấp lên Quản Lý thành công",
    demoteSuccess: "Người dùng đã được giáng cấp xuống Giáo Viên thành công",
    roleChangeError: "Không thể thay đổi vai trò người dùng: {error}",
    // New entries for combined interface
    combined: {
      title: "Quản Lý Người Dùng & Nhân Viên",
      description:
        "Quản lý tài khoản người dùng, hồ sơ nhân viên và phân công vai trò trong một nơi",
      userTab: "Tài Khoản Người Dùng",
      staffTab: "Hồ Sơ Nhân Viên",
      linkedAccounts: "Tài Khoản Liên Kết",
      userCount: "{count} người dùng",
      staffCount: "{count} nhân viên",
      linkStatus: "Trạng Thái Liên Kết",
      linked: "Đã Liên Kết",
      notLinked: "Chưa Liên Kết",
      linkedTo: "Đã liên kết với: {name}",
      filterUsers: "Lọc người dùng",
      filterStaff: "Lọc nhân viên",
      staffMembers: "Danh Sách Nhân Viên",
      userAccounts: "Tài Khoản Người Dùng",
      assignStaff: "Phân Công Hồ Sơ Nhân Viên",
      createStaffProfile: "Tạo Hồ Sơ Nhân Viên",
      selectStaffToLink: "Chọn Nhân Viên để Liên Kết",
      linkToNewStaff: "Liên Kết với Hồ Sơ Nhân Viên Mới",
      unassignedUsers: "Người Dùng Chưa Được Phân Công",
      unlinkedStaff: "Hồ Sơ Nhân Viên Chưa Liên Kết",
      linkedUsers: "Người Dùng Đã Liên Kết",
      userDetails: "Thông Tin Người Dùng",
      staffDetails: "Thông Tin Nhân Viên",
      linkActionSuccess: "Liên kết người dùng với hồ sơ nhân viên thành công",
      unlinkActionSuccess: "Hủy liên kết người dùng với hồ sơ nhân viên thành công",
      userIdCopied: "ID người dùng đã được sao chép vào bộ nhớ tạm",
      staffIdCopied: "ID nhân viên đã được sao chép vào bộ nhớ tạm",
    },
  },
  // Class Assignment
  classAssignment: {
    title: "Phân Công Lớp Học",
    teachers: "Giáo Viên",
    teachingAssistants: "Trợ Giảng",
    noTeachers: "Chưa có giáo viên nào được phân công",
    noTAs: "Chưa có trợ giảng nào được phân công",
    addUser: "Thêm Người Dùng",
    selectUser: "Chọn người dùng",
    selectStaff: "Chọn nhân viên",
    role: "Vai Trò",
    teacher: "Giáo Viên",
    ta: "Trợ Giảng",
    admin: "Quản Trị",
    unknown: "Không xác định",
    add: "Thêm",
    alreadyAssigned: "Người dùng này đã được phân công cho lớp này",
    userAdded: "Phân công người dùng thành công",
    userRemoved: "Đã xóa người dùng khỏi lớp học",
    addError: "Không thể thêm người dùng",
    removeError: "Không thể xóa người dùng",
    globalClass: "Lớp Học Toàn Cục (tất cả quản lý đều nhìn thấy)",
    privateClass: "Lớp Học Riêng",
    makeGlobal: "Đặt thành toàn cục cho tất cả quản lý",
    globalSuccess: "Lớp học giờ đây hiển thị với tất cả quản lý",
    privateSuccess: "Lớp học giờ đây là riêng tư",
    globalError: "Không thể thay đổi khả năng hiển thị của lớp học",
    managerView: "Chế Độ Xem Quản Lý",
  },
  // Staff Roles Management
  staffRoles: {
    title: "Quản Lý Vai Trò Nhân Viên",
    description: "Tạo và quản lý tài khoản, vai trò và quyền của nhân viên",
    createStaff: "Tạo Nhân Viên Mới",
    createStaffAccount: "Tạo Tài Khoản Nhân Viên",
    staffList: "Danh Sách Nhân Viên",
    search: "Tìm theo tên hoặc email",
    name: "Tên",
    fullName: "Họ Tên",
    email: "Email",
    role: "Vai Trò",
    baseRate: "Mức Lương Cơ Bản",
    contactInfo: "Thông Tin Liên Hệ",
    phoneNumber: "Số Điện Thoại",
    zalo: "Zalo",
    facebook: "Facebook",
    position: "Vị Trí",
    positionTeacher: "Giáo Viên",
    positionTA: "Trợ Giảng",
    positionAdmin: "Nhân Viên Hành Chính",
    baseRateHelp: "Mức lương cơ bản theo giờ (VND)",
    actions: "Hành Động",
    noStaff: "Không tìm thấy nhân viên",
    unnamed: "Nhân Viên Chưa Đặt Tên",
    roleManager: "Quản Lý",
    roleTeacher: "Giáo Viên",
    edit: "Sửa",
    delete: "Xóa",
    addStaff: "Thêm Nhân Viên Mới",
    editStaff: "Sửa Hồ Sơ Nhân Viên",
    staffDetails: "Chi Tiết Nhân Viên",
    displayName: "Tên Hiển Thị",
    defaultRate: "Mức Lương Cơ Bản (VND)",
    assignUserAccount: "Gán Tài Khoản Người Dùng",
    createUserAccount: "Tạo Tài Khoản Người Dùng",
    userAccountHelp:
      "Điều này sẽ tạo một tài khoản người dùng mới được liên kết với hồ sơ nhân viên này",
    selectUser: "Chọn Tài Khoản Người Dùng",
    noUserAccount: "Không Có Tài Khoản Người Dùng",
    createSuccess: "Đã tạo hồ sơ nhân viên thành công",
    updateSuccess: "Đã cập nhật hồ sơ nhân viên thành công",
    deleteSuccess: "Đã xóa hồ sơ nhân viên thành công",
    promotedSuccess: "{name} đã được thăng cấp lên Quản Lý",
    demotedSuccess: "{name} đã được giáng cấp xuống Giáo Viên",
    roleChangeError: "Không thể thay đổi vai trò người dùng: {error}",
    refreshError: "Không thể làm mới dữ liệu",
    error: "Đã xảy ra lỗi. Vui lòng thử lại.",
    confirmDelete: "Xóa Hồ Sơ Nhân Viên",
    confirmDeleteDescription:
      "Bạn có chắc muốn xóa hồ sơ nhân viên này? Hành động này không thể hoàn tác.",
    staffTab: "Nhân Viên",
    rolesTab: "Vai Trò Người Dùng",
    staffManagement: "Quản Lý Nhân Viên",
    accessDenied: "Truy Cập Bị Từ Chối",
    managerRoleRequired: "Bạn phải là quản lý để truy cập phần này.",
    promoteUser: "Thăng Cấp Người Dùng",
    demoteUser: "Giáng Cấp Người Dùng",
    roleChangeWarning: "Điều này sẽ thay đổi quyền hạn và mức độ truy cập của người dùng.",
    confirmPromotion: "Bạn có chắc muốn thăng cấp {name} lên vai trò Quản Lý?",
    confirmDemotion: "Bạn có chắc muốn giáng cấp {name} xuống vai trò Giáo Viên?",
    promote: "Thăng Cấp",
    demote: "Giáng Cấp",
    promotedSuccess: "{name} đã được thăng cấp lên Quản Lý",
    demotedSuccess: "{name} đã được giáng cấp xuống Giáo Viên",
    unlinkSuccess: "Đã hủy liên kết người dùng thành công",
    unlinkError: "Không thể hủy liên kết người dùng: {error}",
    active: "Đang Hoạt Động",
    inactive: "Không Hoạt Động",
    notProvided: "Không cung cấp",
    accountStatus: "Trạng Thái Tài Khoản",
    linked: "Đã liên kết với tài khoản người dùng",
    noAccount: "Không có tài khoản người dùng",
    unlink: "Hủy Liên Kết",
    link: "Liên Kết Tài Khoản",
    createFirstStaff: "Tạo Nhân Viên Đầu Tiên",
    noStaffDescription: "Tạo nhân viên đầu tiên của bạn để bắt đầu.",
    roles: "Vai Trò Người Dùng",
    roleUser: "Người Dùng",
    currentRole: "Vai Trò Hiện Tại",
    selectUserHelp: "Chọn tài khoản người dùng để liên kết với nhân viên này",
  },
  // Staff Management
  staff: {
    title: "Quản Lý Nhân Viên",
    description: "Tạo và quản lý nhân viên cho việc phân công lớp học",
    addStaff: "Thêm Nhân Viên",
    editStaff: "Sửa Thông Tin Nhân Viên",
    staffDetails: "Chi Tiết Nhân Viên",
    name: "Tên",
    fullName: "Họ Tên",
    email: "Email",
    phone: "Số Điện Thoại",
    position: "Vị Trí",
    baseRate: "Mức Lương Cơ Bản",
    status: "Trạng Thái",
    actions: "Thao Tác",
    active: "Đang Hoạt Động",
    inactive: "Không Hoạt Động",
    noStaffFound: "Không tìm thấy nhân viên nào",
    positions: {
      teacher: "Giáo Viên",
      teachingAssistant: "Trợ Giảng",
      adminStaff: "Nhân Viên Hành Chính",
    },
    contactInfo: "Thông Tin Liên Hệ",
    address: "Địa Chỉ",
    emergencyContact: "Liên Hệ Khẩn Cấp",
    socialMedia: "Mạng Xã Hội",
    facebook: "Facebook",
    zalo: "Zalo",
    notes: "Ghi Chú",
    saveSuccess: "Lưu thông tin nhân viên thành công",
    deleteSuccess: "Xóa nhân viên thành công",
    deleteConfirm: "Bạn có chắc chắn muốn xóa nhân viên này?",
    statusToggle: "Thay đổi trạng thái",
    setActive: "Đặt là đang hoạt động",
    setInactive: "Đặt là không hoạt động",
    userAccount: "Tài Khoản Người Dùng",
    linkedAccount: "Tài Khoản Đã Liên Kết",
    noAccount: "Không Có Tài Khoản",
    link: "Liên Kết",
    unlink: "Hủy Liên Kết",
    createAccount: "Tạo Tài Khoản",
    linkToAccount: "Liên Kết với Tài Khoản Người Dùng",
    selectUser: "Chọn Người Dùng",
    noAvailableUsers: "Không có người dùng nào để liên kết",
    availableUsers: "Người Dùng Có Sẵn",
    userAlreadyLinked: "Người dùng này đã được liên kết với hồ sơ nhân viên khác",
    linkSuccess: "Liên kết người dùng với hồ sơ nhân viên thành công",
    linkError: "Không thể liên kết người dùng với hồ sơ nhân viên",
    unlinkSuccess: "Hủy liên kết người dùng khỏi hồ sơ nhân viên thành công",
    unlinkConfirm: "Hủy Liên Kết Tài Khoản Người Dùng",
    unlinkConfirmMessage:
      "Bạn có chắc chắn muốn hủy liên kết tài khoản người dùng này khỏi hồ sơ nhân viên? Tài khoản này sẽ không còn liên kết với hồ sơ nhân viên nữa.",
    assignments: "Phân Công Lớp Học",
    noAssignments: "Không có lớp học nào được phân công cho nhân viên này",
    asTeacher: "Với Vai Trò Giáo Viên",
    asTA: "Với Vai Trò Trợ Giảng",
    customRates: "Mức Lương Tùy Chỉnh",
    baseRateHelp: "Mức lương cơ bản theo giờ",
    customRateHelp: "Mức lương tùy chỉnh cho lớp học cụ thể này",
    tabs: {
      all: "Tất Cả Nhân Viên",
      active: "Đang Hoạt Động",
      inactive: "Không Hoạt Động",
    },
    search: "Tìm kiếm theo tên, số điện thoại hoặc email",
    selectUserHelp: "Chọn tài khoản người dùng để liên kết với nhân viên này",
    unlinkConfirm: "Bạn có chắc chắn muốn hủy liên kết nhân viên này khỏi tài khoản người dùng?",
    unlinkSuccess: "Hủy liên kết người dùng thành công",
    linkSuccess: "Liên kết người dùng thành công",
    errors: {
      nameRequired: "Tên nhân viên là bắt buộc",
      phoneFormat: "Số điện thoại không hợp lệ",
      emailFormat: "Email không hợp lệ",
      rateNotNegative: "Mức lương không thể là số âm",
      saveFailed: "Lưu thông tin nhân viên thất bại",
      deleteFailed: "Xóa nhân viên thất bại",
      linkFailed: "Liên kết nhân viên với tài khoản thất bại",
      unlinkFailed: "Hủy liên kết nhân viên khỏi tài khoản thất bại",
      notFound: "Không tìm thấy nhân viên",
    },
  },
  validation: {
    required: "Trường này là bắt buộc",
    invalidEmail: "Vui lòng nhập địa chỉ email hợp lệ",
    invalidRate: "Vui lòng nhập mức lương hợp lệ",
    invalidPhone: "Vui lòng nhập số điện thoại hợp lệ",
    passwordMismatch: "Mật khẩu không khớp",
    passwordTooShort: "Mật khẩu phải có ít nhất 6 ký tự",
  },
  roles: {
    manager: "Quản Lý",
    teacher: "Giáo Viên",
    teachingAssistant: "Trợ Giảng",
    staff: "Nhân Viên",
    user: "Người Dùng",
  },
  errors: {
    staffNotFound: "Không tìm thấy nhân viên",
    assignmentFailed: "Không thể phân công nhân viên cho lớp học",
  },
  staffAssignment: {
    classAssignments: "Phân Công Lớp Học",
    baseRate: "Mức Lương Cơ Bản",
    customRate: "Mức Lương Tùy Chỉnh",
    useCustomRate: "Sử Dụng Mức Lương Tùy Chỉnh",
    noClassesAssigned: "Không có lớp học nào được phân công",
    confirmRemoval: "Xóa Khỏi Lớp Học",
    confirmRemovalContent: "Bạn có chắc muốn xóa {staff} khỏi {className}?",
    assignStaffToClass: "Phân Công Nhân Viên Cho {className}",
    linkStaffToClass: "Liên Kết Nhân Viên Với Lớp Học",
    selectStaff: "Chọn Nhân Viên",
    selectClass: "Chọn Lớp Học",
    selectRole: "Chọn Vai Trò",
    staffAdded: "Phân công nhân viên cho lớp học thành công",
    staffRemoved: "Đã xóa nhân viên khỏi lớp học",
    hourlyRate: "Mức Lương Theo Giờ",
    rateInfo: "Sử dụng mức lương cơ bản trừ khi có mức lương tùy chỉnh",
    overrideRate: "Ghi đè mức lương cơ bản",
    assignmentDate: "Ngày Phân Công",
    removing: "Đang xóa phân công nhân viên...",
    adding: "Đang thêm phân công nhân viên...",
  },
};

// Create translations object
const translations = { en, vi };

// Function to get translation - MODIFIED to always return Vietnamese
export const getTranslation = (language, key) => {
  const keys = key.split(".");
  // Always use Vietnamese translations regardless of the language parameter
  let translation = translations["vi"];

  for (const k of keys) {
    translation = translation[k];
    if (!translation) {
      // If translation not found in Vietnamese, try English as fallback
      let fallback = translations["en"];
      for (const fallbackKey of keys) {
        fallback = fallback[fallbackKey];
        if (!fallback) return key; // Return the key itself if even English doesn't have it
      }
      return fallback;
    }
  }

  return translation;
};

export default translations;
